import { ReapplyBlockedReason } from "~/gql/graphqlBase.generated";
import {
	type GetReapplyRecipesPreviewQuery,
	type ReapplyErrorFieldsFragment
} from "~/gql/queries/produce/reapplyRecipes.generated";

type Preview = GetReapplyRecipesPreviewQuery["reapplyRecipesPreview"][number];
type ReapplyEligibility = Pick<Preview, "blockedReason" | "recipeDeleted">;

export const REAPPLY_JOB_NOT_ACTIVE = "JOB_NOT_ACTIVE";
export const REAPPLY_RECIPE_SWITCH_NOT_SELECTED = "RECIPE_SWITCH_NOT_SELECTED";

const BLOCKED_REASON_LABEL_KEYS: Record<ReapplyBlockedReason, string> = {
	[ReapplyBlockedReason.JobPosted]: "schedule.reapplyRecipes.blocked.jobPosted",
	[ReapplyBlockedReason.JobStarted]: "schedule.reapplyRecipes.blocked.jobStarted",
	[ReapplyBlockedReason.LotNotActive]: "schedule.reapplyRecipes.blocked.lotNotActive",
	[ReapplyBlockedReason.NoRecipeCovers]: "schedule.reapplyRecipes.blocked.noRecipeCovers"
};

const blockedReasonLabelKey = (reason: ReapplyBlockedReason): string => BLOCKED_REASON_LABEL_KEYS[reason];

export const describeBlockedReason = (
	reason: ReapplyBlockedReason,
	message: string | null | undefined,
	translate: (key: string) => string
): string => {
	const label = translate(blockedReasonLabelKey(reason));
	const normalize = (text: string): string => text.trim().replace(/\.$/, "").toLowerCase();
	return message && normalize(message) !== normalize(label) ? `${label}. ${message}` : label;
};

export const isPreviewReapplicable = (preview: ReapplyEligibility): boolean =>
	!preview.recipeDeleted && !preview.blockedReason;

export const areAllJobsBlocked = (previews: ReapplyEligibility[]): boolean =>
	previews.some((preview) => preview.blockedReason) && !previews.some(isPreviewReapplicable);

export const findBlockedChildReason = (
	previews: Iterable<Pick<Preview, "affectedChildJobs">>,
	childJobId: string
): ReapplyBlockedReason | null => {
	for (const preview of previews) {
		const child = preview.affectedChildJobs.find((candidate) => candidate.productionJobId === childJobId);
		if (child) return child.blockedReason ?? null;
	}
	return null;
};

export const isJobNotActiveError = (error: Pick<ReapplyErrorFieldsFragment, "code">): boolean =>
	error.code === REAPPLY_JOB_NOT_ACTIVE;

export const isRecipeSwitchNotSelectedError = (error: Pick<ReapplyErrorFieldsFragment, "code">): boolean =>
	error.code === REAPPLY_RECIPE_SWITCH_NOT_SELECTED;

export default blockedReasonLabelKey;
